import React from 'react';
import { Clock, Check, Plus, Users } from 'lucide-react';
import { normalizeAddonOptions } from '../../utils/addonOptions';

const money = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const listOf = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => (typeof item === 'string' ? item : item?.name || item?.text || ''))
    .map((item) => String(item).trim())
    .filter(Boolean);

const ServicePackageCard = ({ package: pkg, onSelect }) => {
  const includes = listOf(pkg.includes_items);
  const features = listOf(pkg.package_features);
  const addons = normalizeAddonOptions(pkg.addon_options).filter((row) => row.name);
  const duration = pkg.duration_hours ? `${Number(pkg.duration_hours)} hours` : null;
  const guests = pkg.max_guests ? `Up to ${pkg.max_guests} guests` : null;
  const priceLabel = pkg.price_type === 'hourly' ? 'per hour' : 'per event';

  return (
    <button
      type="button"
      onClick={() => onSelect?.(pkg)}
      className="w-full rounded-2xl border border-m-border-light bg-m-surface p-5 text-left transition-colors hover:border-m-ink sm:p-6"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h4 className="text-lg font-medium text-m-ink">
            {pkg.package_name || 'Package'}
          </h4>
          <p className="mt-1 text-sm leading-relaxed text-m-ink-muted">
            {pkg.description || 'No description available'}
          </p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-m-ink-muted">
            {duration ? (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {duration}
              </span>
            ) : null}
            {guests ? (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {guests}
              </span>
            ) : null}
          </div>
        </div>
        <div className="sm:text-right">
          <div className="text-2xl font-medium tracking-tight text-m-ink">
            ${money(pkg.base_price)}
          </div>
          <div className="text-xs text-m-ink-faint">{priceLabel}</div>
          <div className="mt-2 text-xs font-medium text-m-ink">Select package</div>
        </div>
      </div>

      {includes.length > 0 ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-m-ink-faint">
            Included
          </p>
          <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {includes.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-m-ink">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {features.length > 0 ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-m-ink-faint">
            Features
          </p>
          <ul className="flex flex-wrap gap-2">
            {features.map((item) => (
              <li
                key={item}
                className="rounded-full border border-m-border-light px-2.5 py-1 text-xs text-m-ink-muted"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {addons.length > 0 ? (
        <div className="mt-4 border-t border-m-border-light pt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-m-ink-faint">
            Optional add-ons
          </p>
          <ul className="space-y-1.5">
            {addons.map((addon) => (
              <li
                key={`${addon.name}-${addon.price}`}
                className="flex items-start justify-between gap-3 text-sm"
              >
                <span className="inline-flex min-w-0 items-start gap-2 text-m-ink">
                  <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-m-ink-muted" />
                  <span>
                    {addon.name}
                    {addon.description ? (
                      <span className="block text-xs text-m-ink-faint">{addon.description}</span>
                    ) : null}
                  </span>
                </span>
                <span className="shrink-0 font-medium tabular-nums text-m-ink">
                  +${money(addon.price)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </button>
  );
};

export default ServicePackageCard;
